// BeneAI-02 — assistant message content_json parser.
//
// Walks the structured ``content_json`` payload the runtime persists
// onto an assistant message and produces an ordered list of typed
// blocks the message list renderer iterates.
//
// Order: text first (the model's prose), then tool_use cards in the
// model's emitted order, then tool_result cards keyed back to the
// matching tool_use, then any pending_confirmations stamped by the
// confirmation gate, then any receipts stamped by Confirm / Cancel.
// This order matches the user's mental model of "BeneAI talked,
// then did things, then asked me to confirm, then reported back."

import type {
  BeneAIAssistantBlock,
  BeneAIErrorCode,
  BeneAIMessage,
  BeneAIPendingConfirmationBlock,
  BeneAIReceiptBlock,
  BeneAIRiskLevel,
  BeneAIConfirmationStatus,
  BeneAIToolResultBlock,
  BeneAIToolUseBlock,
} from "./types";


type Json = Record<string, unknown>;


export function parseAssistantContent(
  msg: BeneAIMessage,
): BeneAIAssistantBlock[] {
  const out: BeneAIAssistantBlock[] = [];
  const cj = (msg.content_json ?? {}) as Json;
  // Synthetic error rows minted by useBeneAIConversation when the
  // backend returned a 503/error payload. Skip the regular text-block
  // path so the renderer can show an error-tone bubble instead of a
  // normal assistant reply.
  if (cj.kind === "error") {
    const code = typeof cj.error_code === "string"
      ? (cj.error_code as BeneAIErrorCode)
      : "unknown";
    const message = typeof cj.message === "string"
      ? cj.message
      : (msg.content || "Something went wrong.");
    out.push({ kind: "error", code, message });
    return out;
  }
  if (msg.content && msg.content.trim().length > 0) {
    out.push({ kind: "text", text: msg.content });
  }
  for (const b of parseToolUses(cj)) {
    out.push({ kind: "tool_use", block: b });
  }
  for (const b of parseToolResults(cj)) {
    out.push({ kind: "tool_result", block: b });
  }
  for (const b of parsePendingConfirmations(cj)) {
    out.push({ kind: "pending_confirmation", block: b });
  }
  for (const b of parseReceipts(cj)) {
    out.push({ kind: "receipt", block: b });
  }
  return out;
}


function parseToolUses(cj: Json): BeneAIToolUseBlock[] {
  const raw = (cj.tool_uses ?? []) as unknown;
  if (!Array.isArray(raw)) return [];
  const blocks: BeneAIToolUseBlock[] = [];
  for (const row of raw as Json[]) {
    if (typeof row !== "object" || row === null) continue;
    const id = stringOrEmpty(row.tool_use_id);
    const name = stringOrEmpty(row.tool_name);
    if (!id || !name) continue;
    const input = (row.input ?? {}) as Json;
    blocks.push({
      tool_use_id: id, tool_name: name,
      input: isObject(input) ? input : {},
    });
  }
  return blocks;
}


function parseToolResults(cj: Json): BeneAIToolResultBlock[] {
  const raw = (cj.tool_results ?? []) as unknown;
  if (!Array.isArray(raw)) return [];
  const blocks: BeneAIToolResultBlock[] = [];
  for (const row of raw as Json[]) {
    if (typeof row !== "object" || row === null) continue;
    const id = stringOrEmpty(row.tool_use_id);
    const name = stringOrEmpty(row.tool_name);
    const outcomeRaw = stringOrEmpty(row.outcome);
    if (!id || !name) continue;
    const outcome: BeneAIToolResultBlock["outcome"] =
      outcomeRaw === "success" || outcomeRaw === "error"
        || outcomeRaw === "rejected"
        ? outcomeRaw
        : "error";
    blocks.push({
      tool_use_id: id,
      tool_name: name,
      outcome,
      output_json: isObject(row.output_json) ? row.output_json : null,
      error_code: nullableString(row.error_code),
      error_message: nullableString(row.error_message),
    });
  }
  return blocks;
}


function parsePendingConfirmations(
  cj: Json,
): BeneAIPendingConfirmationBlock[] {
  const raw = (cj.pending_confirmations ?? []) as unknown;
  if (!Array.isArray(raw)) return [];
  const blocks: BeneAIPendingConfirmationBlock[] = [];
  for (const row of raw as Json[]) {
    if (typeof row !== "object" || row === null) continue;
    const confirmation_id = stringOrEmpty(row.confirmation_id);
    if (!confirmation_id) continue;
    blocks.push({
      confirmation_id,
      tool_call_id: stringOrEmpty(row.tool_call_id),
      tool_name: stringOrEmpty(row.tool_name),
      summary: stringOrEmpty(row.summary),
      target_kind: stringOrEmpty(row.target_kind),
      target_id: nullableString(row.target_id),
      risk_level: asRiskLevel(row.risk_level),
      status: asConfirmationStatus(row.status),
      input_json: isObject(row.input_json) ? row.input_json : {},
    });
  }
  return blocks;
}


function parseReceipts(cj: Json): BeneAIReceiptBlock[] {
  const raw = (cj.receipts ?? []) as unknown;
  if (!Array.isArray(raw)) return [];
  const blocks: BeneAIReceiptBlock[] = [];
  for (const row of raw as Json[]) {
    if (typeof row !== "object" || row === null) continue;
    if (row.kind !== "tool_receipt") continue;
    const outcomeRaw = stringOrEmpty(row.outcome);
    const outcome: BeneAIReceiptBlock["outcome"] =
      outcomeRaw === "success" || outcomeRaw === "failed"
        || outcomeRaw === "cancelled"
        ? outcomeRaw
        : "failed";
    blocks.push({
      kind: "tool_receipt",
      confirmation_id: stringOrEmpty(row.confirmation_id),
      tool_use_id: stringOrEmpty(row.tool_use_id),
      tool_name: stringOrEmpty(row.tool_name),
      outcome,
      summary: stringOrEmpty(row.summary),
      output_json: isObject(row.output_json) ? row.output_json : null,
      error_message: nullableString(row.error_message),
      receipt_token_id: nullableString(row.receipt_token_id),
      undo_available: row.undo_available === true,
      timestamp: stringOrEmpty(row.timestamp),
    });
  }
  return blocks;
}


function stringOrEmpty(v: unknown): string {
  return typeof v === "string" ? v : "";
}


function nullableString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}


function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}


function asRiskLevel(v: unknown): BeneAIRiskLevel {
  return v === "destructive" || v === "bulk" ? v : "normal";
}


function asConfirmationStatus(v: unknown): BeneAIConfirmationStatus {
  switch (v) {
    case "approved":
    case "rejected":
    case "expired":
    case "failed":
      return v;
    default:
      return "pending";
  }
}
